/**
 * "Mix and save" quantity bar for the product page.
 *
 * Counts the spools the buyer is choosing (the quantity box) plus the spools already in the cart for the same product
 * family (so mixing colours counts), and flows a yellow line along a bar with milestones, like filament being fed.
 * It only shows a preview: the real discount comes from a Shopify discount. No sound. No library.
 *
 * Position rule: the milestones sit at equal distances (1/n, 2/n, ... n/n of the bar). Between two milestones the line
 * moves in proportion to the spools, so with milestone 1 at 3 spools, 1 spool is 1/3 of the way and 3 spools is on it.
 *
 * Celebration rule: every new milestone reached by adding spools gets a celebration that is bigger than the one before.
 * The number of levels comes from the milestones (theme settings); the size scales with the milestone index.
 *
 * Two sources for the total (data-source): "quantity" (the product page: quantity box plus cart) and "external" (the
 * Custom Pack sheet, which sends its total with the document event surespool:pack-total).
 */

const FLOW_MS = 600;
const COUNT_MS = 260;
const FADE_MS = 240;
const EDGE = 0.8; // how close (in % of the bar) the line must be to a milestone to count as arrived
const YELLOW = '#FFD400';
const INK = '#14181C';

const easeOut = (/** @type {number} */ t) => 1 - Math.pow(1 - t, 3);
const rand = (/** @type {number} */ a, /** @type {number} */ b) => a + Math.random() * (b - a);

/**
 * What each kind of celebration does. 'small' is the first milestone, 'medium' the ones in between, 'big' the top one.
 * Durations are about 0.9 s, 1.4 s and 2 s.
 */
const PROFILES = {
  small: { rings: 1, ringGap: 0, waves: [{ at: 0, count: 14 }], reach: [30, 70], life: [650, 900], pop: 1, vibrate: [15] },
  medium: {
    rings: 2,
    ringGap: 220,
    waves: [
      { at: 0, count: 20 },
      { at: 260, count: 16 },
    ],
    reach: [45, 115],
    life: [850, 1150],
    pop: 1,
    vibrate: [15, 70, 15],
  },
  big: {
    rings: 3,
    ringGap: 200,
    waves: [
      { at: 0, count: 34 },
      { at: 220, count: 28 },
      { at: 480, count: 18 },
    ],
    reach: [60, 190],
    life: [1100, 1600],
    pop: 1,
    vibrate: [30, 60, 30, 60, 70],
  },
};

class SureSpoolMixSave extends HTMLElement {
  /** @type {{ qty: number, price: number }[]} */
  milestones = [];
  /** @type {number | null} */
  lastTotal = null;
  cartQty = 0;
  /** Where the line is drawn right now, in percent of the bar (0 to 100). */
  shown = 0;
  target = 0;
  /** @type {number} */
  flowFrame = 0;
  flowStart = 0;
  flowFrom = 0;
  /** True while the last change came from adding spools, so a new milestone may celebrate. */
  movingUp = false;
  /** The one milestone (the highest newly reached) that may celebrate in the current move. */
  celebrateIndex = -1;
  /** @type {AbortController | null} */
  abort = null;
  /** @type {MutationObserver | null} */
  observer = null;
  /** @type {number} */
  timer = 0;
  /** @type {number} */
  countFrame = 0;
  /** @type {number} */
  pctFrame = 0;
  /** Milestones that are drawn as reached (kept in step with the line, not with the number). */
  unlockedSet = new Set();
  /** Milestones that already had their moment. Cleared for a milestone when the user drops below it. */
  celebrated = new Set();
  lastBestPct = 0;
  pctBusy = false;
  /** The total sent by the Custom Pack sheet (source "external" only). */
  externalTotal = 0;

  get #external() {
    return this.dataset.source === 'external';
  }

  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    try {
      this.milestones = JSON.parse(this.dataset.milestones || '[]');
    } catch (error) {
      this.milestones = [];
    }
    this.milestones.sort((a, b) => a.qty - b.qty);
    this.lastBestPct = this.#basePrice;

    if (this.#external) {
      // The Custom Pack sheet owns the numbers and tells the bar its total.
      document.addEventListener(
        'surespool:pack-total',
        (/** @type {Event} */ event) => {
          this.externalTotal = Math.max(0, Number(/** @type {CustomEvent} */ (event).detail?.total) || 0);
          this.#schedule();
        },
        { signal }
      );
    } else {
      const scope = this.closest('.shopify-section') || document;
      scope.addEventListener('input', this.#onQuantityEvent, { signal });
      scope.addEventListener('change', this.#onQuantityEvent, { signal });
      scope.addEventListener('click', this.#onQuantityEvent, { signal });
      scope.addEventListener('pointerup', this.#onQuantityEvent, { signal });
      document.addEventListener('quantity-selector:update', this.#schedule, { signal });
      this.#watchCart(signal);
    }

    // The product section can be re-rendered when the colour changes. Repaint from the saved state when that happens.
    this.observer = new MutationObserver(this.#onMutation);
    this.observer.observe(this, { childList: true, subtree: true });

    if (!this.#external) this.#refreshCart().finally(() => this.#render(true));
    this.#render(false);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.timer);
    cancelAnimationFrame(this.flowFrame);
    cancelAnimationFrame(this.countFrame);
    cancelAnimationFrame(this.pctFrame);
  }

  get #reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** @returns {HTMLInputElement | null} */
  get #input() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('quantity-selector-component input[type="number"], quantity-selector-component input');
  }

  get #choosing() {
    if (this.#external) return this.externalTotal;
    const input = this.#input;
    const value = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /** The price per spool before any tier is reached (theme settings). */
  get #basePrice() {
    return Number(this.dataset.basePrice) || 0;
  }

  get #prefix() {
    return (this.dataset.prefix || '').toLowerCase();
  }

  get #countSample() {
    return this.dataset.countSample === 'true';
  }

  /** Position of milestone `index` along the bar, in percent. Equal distances. */
  #stopPos(/** @type {number} */ index) {
    return ((index + 1) / this.milestones.length) * 100;
  }

  /** Where the line must end for a number of spools, in percent. Proportional between milestones. Never above 100. */
  #posFor(/** @type {number} */ total) {
    if (total <= 0) return 0;
    let prevQty = 0;
    let prevPos = 0;
    for (let i = 0; i < this.milestones.length; i++) {
      const qty = this.milestones[i].qty;
      const pos = this.#stopPos(i);
      if (total <= qty) {
        const span = qty - prevQty;
        return span > 0 ? prevPos + ((pos - prevPos) * (total - prevQty)) / span : pos;
      }
      prevQty = qty;
      prevPos = pos;
    }
    return 100;
  }

  /** Which celebration a milestone gets: the first is small, the last is big, the ones between are medium. */
  #profileFor(/** @type {number} */ index) {
    const last = this.milestones.length - 1;
    if (index === last) return PROFILES.big;
    if (index === 0) return PROFILES.small;
    // A middle milestone grows a little with its place in the row.
    const share = last > 1 ? index / last : 0.5;
    const scale = 0.85 + share * 0.3;
    return {
      ...PROFILES.medium,
      waves: PROFILES.medium.waves.map((wave) => ({ at: wave.at, count: Math.round(wave.count * scale) })),
    };
  }

  #onQuantityEvent = (/** @type {Event} */ event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;
    if (!target.closest('quantity-selector-component')) return;
    this.#schedule();
  };

  #onMutation = (/** @type {MutationRecord[]} */ records) => {
    // Ignore the bar's own writes (text updates, effects). Only outside changes, such as a re-render, repaint it.
    const outside = records.some((record) => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      if (target?.closest('[data-mix-total], [data-mix-percent], [data-mix-help], [data-mix-state], .ss-mix__fx')) return false;
      const nodes = [...record.addedNodes, ...record.removedNodes];
      return !nodes.every((node) => node instanceof Element && /ss-mix__(piece|ripple|fx|sparkle|sweep)/.test(String(node.className)));
    });
    if (outside) this.#schedule();
  };

  #schedule = () => {
    clearTimeout(this.timer);
    // Wait a moment so the quantity component has written its new value (also when + is held or pressed fast).
    this.timer = window.setTimeout(() => this.#render(true), 20);
  };

  /** After any cart request from this page, read the cart again. */
  #watchCart(/** @type {AbortSignal} */ signal) {
    if (!window.__ssMixFetchPatched) {
      window.__ssMixFetchPatched = true;
      const original = window.fetch;
      window.fetch = async function patchedFetch(...args) {
        const response = await original.apply(this, args);
        try {
          const url = String(args[0] instanceof Request ? args[0].url : args[0]);
          if (/\/cart\/(add|change|update|clear)/.test(url)) {
            document.dispatchEvent(new CustomEvent('surespool:cart-changed', { detail: { url } }));
          }
        } catch (error) {
          // Never block the page because of this helper.
        }
        return response;
      };
    }

    document.addEventListener(
      'surespool:cart-changed',
      async (/** @type {Event} */ event) => {
        const url = /** @type {CustomEvent} */ (event).detail?.url || '';
        await this.#refreshCart();
        if (/\/cart\/add/.test(url)) this.#resetChoosing();
        this.#render(true);
      },
      { signal }
    );
  }

  async #refreshCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const prefix = this.#prefix;
      const productHandle = this.dataset.productHandle || '';
      this.cartQty = cart.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => {
        const handle = String(item.handle || '').toLowerCase();
        const sameFamily = (prefix && handle.startsWith(prefix)) || handle === productHandle;
        const isSample = handle.includes('sample');
        if (!sameFamily) return sum;
        if (isSample && !this.#countSample) return sum;
        return sum + item.quantity;
      }, 0);
    } catch (error) {
      this.cartQty = 0;
    }
  }

  /** After a spool is added, the quantity box goes back to its minimum so the same spool is not counted twice. */
  #resetChoosing() {
    const input = this.#input;
    if (!input) return;
    const min = parseInt(input.dataset.min || '1', 10) || 1;
    input.value = String(min);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Writes the line position to the page. Safe to call at any time. */
  #paint() {
    const bar = this.querySelector('.ss-mix__bar');
    if (bar instanceof HTMLElement) bar.style.setProperty('--ss-mix-fill', `${this.shown}%`);
    this.classList.toggle('has-fill', this.shown > 0.2);
    // At the very end the head would sit on top of the last circle, so it fades out there.
    this.classList.toggle('is-full', this.shown >= 99.5);
  }

  /** Draws each milestone as reached or not for the line's current position. Celebrates the chosen one. */
  #syncStops(/** @type {boolean} */ celebrate) {
    const stops = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mix-stop]')]);
    const last = this.milestones.length - 1;
    stops.forEach((stop, index) => {
      if (!this.milestones[index]) return;
      stop.classList.toggle('is-top', index === last);
      const pos = this.#stopPos(index);
      // A milestone is reached when the line is on it and is not heading back below it.
      const reached = this.shown >= pos - EDGE && this.target >= pos - EDGE;
      const was = this.unlockedSet.has(index);
      if (reached === was && stop.classList.contains('is-unlocked') === reached) return;

      if (reached) {
        this.unlockedSet.add(index);
      } else {
        this.unlockedSet.delete(index);
        this.celebrated.delete(index);
      }

      stop.classList.toggle('is-unlocked', reached);
      const state = stop.querySelector('[data-mix-state]');
      const stateText = reached ? this.dataset.unlockedText || 'unlocked' : this.dataset.lockedText || 'locked';
      if (state && state.textContent !== stateText) state.textContent = stateText;

      if (!reached || was) return;
      // Only one celebration per move (the highest milestone passed), and never twice for the same milestone.
      const mayCelebrate = celebrate && index === this.celebrateIndex && !this.celebrated.has(index);
      this.celebrated.add(index);
      if (mayCelebrate && !this.#reduced) this.#celebrate(stop, index);
      if (index === last) this.#syncTopActive();
    });
    this.#syncTopActive();
  }

  #syncTopActive() {
    const last = this.milestones.length - 1;
    this.classList.toggle('is-top-active', last >= 0 && this.unlockedSet.has(last));
    // Nitro level = how many milestones are reached (0 to 3). The CSS makes the strand stronger with each level.
    this.dataset.nitro = String(Math.min(3, this.unlockedSet.size));
  }

  /** The effects layer: a clipped box inside the card, so particles can never cause scrolling or layout shift. */
  #fxLayer() {
    let fx = this.querySelector(':scope > .ss-mix__fx');
    if (!(fx instanceof HTMLElement)) {
      fx = document.createElement('div');
      fx.className = 'ss-mix__fx';
      fx.setAttribute('aria-hidden', 'true');
      this.appendChild(fx);
    }
    return fx;
  }

  /** A short buzz on phones that support it. Skipped when motion is reduced. */
  #vibrate(/** @type {number[]} */ pattern) {
    try {
      if (!this.#reduced && 'vibrate' in navigator && typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
    } catch (error) {
      // Not supported: nothing happens.
    }
  }

  /**
   * The celebration for one milestone. It grows with the level: more rings, more confetti, a wider burst, a longer
   * show. The top level adds streamers, a card-wide light sweep and a border glow.
   */
  #celebrate(/** @type {HTMLElement} */ stop, /** @type {number} */ index) {
    const last = this.milestones.length - 1;
    const isTop = index === last;
    const isFirst = index === 0 && !isTop;
    const profile = this.#profileFor(index);
    const dot = /** @type {HTMLElement} */ (stop.querySelector('[data-mix-dot]') || stop);
    const badge = stop.querySelector('.ss-mix__badge');

    // The circle pops with a spring; the badge bounces (or wiggles) and gets a sparkle.
    stop.classList.remove('is-pop', 'is-bounce', 'is-wiggle');
    void stop.offsetWidth;
    // First and top levels bounce the badge; the levels in between wiggle it once.
    stop.classList.add('is-pop', isFirst || isTop ? 'is-bounce' : 'is-wiggle');
    window.setTimeout(() => stop.classList.remove('is-pop', 'is-bounce', 'is-wiggle'), 650);
    if (badge instanceof HTMLElement) this.#sparkle(badge, isTop);

    for (let i = 0; i < profile.rings; i++) {
      window.setTimeout(() => this.#ripple(dot, isTop ? 3.9 : isFirst ? 2.8 : 3.3), i * profile.ringGap);
    }

    const fx = this.#fxLayer();
    const origin = this.#originIn(dot);
    profile.waves.forEach((wave, waveIndex) => {
      window.setTimeout(() => this.#spawn(fx, origin, wave.count, profile, waveIndex, isFirst), wave.at);
    });

    if (!isFirst) this.#sweepLine(isTop ? 900 : 650);
    if (index >= 1 && !isTop) this.#cardPulse(1.012, 520);
    if (isTop) {
      this.#streamers(fx, origin);
      this.#sweepCard(fx);
      this.#flash();
      this.#cardPulse(1.016, 620);
    }

    this.#vibrate(profile.vibrate);
  }

  /** Where the circle's centre is, measured inside the card. */
  #originIn(/** @type {HTMLElement} */ dot) {
    const card = this.getBoundingClientRect();
    const rect = dot.getBoundingClientRect();
    return { x: rect.left + rect.width / 2 - card.left, y: rect.top + rect.height / 2 - card.top, w: card.width, h: card.height };
  }

  #ripple(/** @type {HTMLElement} */ dot, /** @type {number} */ size) {
    const ring = document.createElement('span');
    ring.className = 'ss-mix__ripple';
    ring.setAttribute('aria-hidden', 'true');
    dot.appendChild(ring);
    const animation = ring.animate(
      [
        { transform: 'scale(1)', opacity: 0.9 },
        { transform: `scale(${size})`, opacity: 0 },
      ],
      { duration: 650, easing: 'ease-out', fill: 'forwards' }
    );
    animation.onfinish = () => ring.remove();
    window.setTimeout(() => ring.remove(), 900);
  }

  /** A tiny 4-point star that appears at the corner of the badge and fades. */
  #sparkle(/** @type {HTMLElement} */ badge, /** @type {boolean} */ big) {
    const star = document.createElement('span');
    star.className = 'ss-mix__sparkle';
    star.setAttribute('aria-hidden', 'true');
    badge.appendChild(star);
    const animation = star.animate(
      [
        { transform: 'scale(0) rotate(0deg)', opacity: 0 },
        { transform: `scale(${big ? 1.5 : 1.15}) rotate(45deg)`, opacity: 1, offset: 0.35 },
        { transform: 'scale(0.2) rotate(90deg)', opacity: 0 },
      ],
      { duration: big ? 900 : 700, easing: 'ease-out', fill: 'forwards' }
    );
    animation.onfinish = () => star.remove();
    window.setTimeout(() => star.remove(), 1100);
  }

  /** Confetti: round and small rectangular pieces in yellow, black and white. */
  #spawn(
    /** @type {HTMLElement} */ fx,
    /** @type {{ x: number, y: number, w: number, h: number }} */ origin,
    /** @type {number} */ count,
    /** @type {typeof PROFILES.small} */ profile,
    /** @type {number} */ waveIndex,
    /** @type {boolean} */ gentle
  ) {
    const colours = [YELLOW, INK, '#FFFFFF', YELLOW, INK];
    for (let i = 0; i < count; i++) {
      const piece = document.createElement('span');
      piece.className = 'ss-mix__piece';
      piece.setAttribute('aria-hidden', 'true');
      const colour = colours[Math.floor(Math.random() * colours.length)];
      const rectangle = !gentle && Math.random() < 0.45;
      const size = rectangle ? [3, 9] : [gentle ? 4 : 4 + waveIndex, gentle ? 7 : 8];
      const w = rectangle ? size[0] : rand(size[0], size[1]);
      const h = rectangle ? size[1] : w;
      piece.style.cssText = `width:${w}px;height:${h}px;background:${colour};border-radius:${rectangle ? '1px' : '50%'};left:${origin.x - w / 2}px;top:${origin.y - h / 2}px;`;
      if (colour === '#FFFFFF') piece.style.boxShadow = '0 0 0 1px rgb(20 24 28 / 0.35)';
      fx.appendChild(piece);

      const angle = rand(0, Math.PI * 2);
      const distance = rand(profile.reach[0], profile.reach[1]);
      const dx = Math.cos(angle) * distance * (gentle ? 1 : 1.25);
      const dy = Math.sin(angle) * distance * 0.8 - rand(8, 26);
      const fall = rand(18, gentle ? 34 : 60);
      const spin = rand(-540, 540);
      const life = rand(profile.life[0], profile.life[1]);
      const animation = piece.animate(
        [
          { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1 },
          { transform: `translate(${dx}px, ${dy}px) rotate(${spin * 0.6}deg) scale(1)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${dx * 1.08}px, ${dy + fall}px) rotate(${spin}deg) scale(0.5)`, opacity: 0 },
        ],
        { duration: life, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'forwards' }
      );
      animation.onfinish = () => piece.remove();
      // Safety: never leave a piece behind if the browser skips the finish event.
      window.setTimeout(() => piece.remove(), life + 250);
    }
  }

  /** Thin streamers that burst up from the circle and drift down across the card (top level only). */
  #streamers(/** @type {HTMLElement} */ fx, /** @type {{ x: number, y: number, w: number, h: number }} */ origin) {
    const count = 9;
    for (let i = 0; i < count; i++) {
      const strip = document.createElement('span');
      strip.className = 'ss-mix__piece';
      strip.setAttribute('aria-hidden', 'true');
      const w = rand(2, 3);
      const h = rand(16, 26);
      strip.style.cssText = `width:${w}px;height:${h}px;background:${i % 2 ? INK : YELLOW};border-radius:1px;left:${origin.x}px;top:${origin.y}px;`;
      fx.appendChild(strip);

      const side = (i / (count - 1)) * 2 - 1; // spread from far left to far right
      const dx = side * origin.w * 0.45 + rand(-20, 20);
      const rise = rand(30, 70);
      const life = rand(1700, 2000);
      const animation = strip.animate(
        [
          { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
          { transform: `translate(${dx * 0.5}px, ${-rise}px) rotate(${rand(120, 240)}deg)`, opacity: 1, offset: 0.3 },
          { transform: `translate(${dx}px, ${origin.h + 30 - origin.y}px) rotate(${rand(380, 720)}deg)`, opacity: 0.9 },
        ],
        { duration: life, easing: 'cubic-bezier(0.3, 0.1, 0.5, 1)', fill: 'forwards' }
      );
      animation.onfinish = () => strip.remove();
      window.setTimeout(() => strip.remove(), life + 250);
    }
  }

  /** A bright shine that runs along the yellow line from its start to the circle. */
  #sweepLine(/** @type {number} */ duration) {
    const fill = this.querySelector('[data-mix-fill]');
    if (!(fill instanceof HTMLElement)) return;
    const sweep = document.createElement('span');
    sweep.className = 'ss-mix__sweep ss-mix__sweep--line';
    sweep.setAttribute('aria-hidden', 'true');
    fill.appendChild(sweep);
    const animation = sweep.animate(
      [{ transform: 'translateX(-100%)' }, { transform: 'translateX(320%)' }],
      { duration, easing: 'ease-in-out', fill: 'forwards' }
    );
    animation.onfinish = () => sweep.remove();
    window.setTimeout(() => sweep.remove(), duration + 250);
  }

  /** A light sweep across the whole card (top level only). */
  #sweepCard(/** @type {HTMLElement} */ fx) {
    const sweep = document.createElement('span');
    sweep.className = 'ss-mix__sweep';
    sweep.setAttribute('aria-hidden', 'true');
    fx.appendChild(sweep);
    const animation = sweep.animate(
      [{ transform: 'translateX(-110%)' }, { transform: 'translateX(250%)' }],
      { duration: 1000, easing: 'ease-in-out', fill: 'forwards' }
    );
    animation.onfinish = () => sweep.remove();
    window.setTimeout(() => sweep.remove(), 1250);
  }

  /** A one-time yellow glow flash on the card border. */
  #flash() {
    this.classList.remove('is-flash');
    void this.offsetWidth;
    this.classList.add('is-flash');
    window.setTimeout(() => this.classList.remove('is-flash'), 1100);
  }

  /** A tiny scale pulse of the whole card. */
  #cardPulse(/** @type {number} */ peak, /** @type {number} */ duration) {
    this.animate([{ transform: 'scale(1)' }, { transform: `scale(${peak})`, offset: 0.4 }, { transform: 'scale(1)' }], {
      duration,
      easing: 'ease-in-out',
    });
  }

  /** Moves the drawn line toward `target`, restarting from where it is now if it is already moving. */
  #flowTo(/** @type {number} */ target, /** @type {boolean} */ animate) {
    cancelAnimationFrame(this.flowFrame);
    this.target = Math.min(100, Math.max(0, target));

    if (!animate || this.#reduced || Math.abs(this.target - this.shown) < 0.05) {
      this.shown = this.target;
      this.classList.remove('is-flowing');
      this.#paint();
      this.#syncStops(false);
      return;
    }

    this.flowFrom = this.shown;
    this.flowStart = performance.now();
    this.classList.add('is-flowing');

    const step = (/** @type {number} */ now) => {
      const t = Math.min(1, (now - this.flowStart) / FLOW_MS);
      this.shown = t >= 1 ? this.target : this.flowFrom + (this.target - this.flowFrom) * easeOut(t);
      this.#paint();
      this.#syncStops(this.movingUp);
      if (t < 1) {
        this.flowFrame = requestAnimationFrame(step);
      } else {
        this.classList.remove('is-flowing');
      }
    };
    this.flowFrame = requestAnimationFrame(step);
  }

  /**
   * Swaps a piece of text with a short fade (no fade when motion is reduced or on the first paint).
   * `bounce` adds a tiny upward bounce, used for the top-level message.
   */
  #setText(/** @type {Element} */ element, /** @type {string} */ text, /** @type {boolean} */ fade, bounce = false) {
    if (element.textContent === text) return;
    element.textContent = text;
    if (!fade || this.#reduced) return;
    const frames = bounce
      ? [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'translateY(-4px)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ]
      : [
          { opacity: 0, transform: 'translateY(4px)' },
          { opacity: 1, transform: 'none' },
        ];
    element.animate(frames, { duration: bounce ? 420 : FADE_MS, easing: 'ease-out' });
  }

  /** Counts the shown number quickly toward the new total. */
  #countTo(/** @type {Element} */ element, /** @type {number} */ to, /** @type {boolean} */ animate) {
    cancelAnimationFrame(this.countFrame);
    const from = parseInt(element.textContent || '0', 10) || 0;
    if (!animate || this.#reduced || from === to) {
      if (element.textContent !== String(to)) element.textContent = String(to);
      return;
    }
    const start = performance.now();
    const step = (/** @type {number} */ now) => {
      const t = Math.min(1, (now - start) / COUNT_MS);
      const value = Math.round(from + (to - from) * easeOut(t));
      if (element.textContent !== String(value)) element.textContent = String(value);
      if (t < 1) this.countFrame = requestAnimationFrame(step);
    };
    this.countFrame = requestAnimationFrame(step);
  }

  /** The discount number at the top level: a bigger count-up with a quick scale. */
  #countPercent(/** @type {Element} */ element, /** @type {number} */ from, /** @type {number} */ to) {
    cancelAnimationFrame(this.pctFrame);
    const template = this.dataset.priceText || '₹[price] each';
    if (this.#reduced || from === to) {
      this.pctBusy = false;
      this.#setText(element, template.replace('[price]', String(to)), false);
      return;
    }
    this.pctBusy = true;
    element.classList.add('ss-mix__big');
    element.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.3)', offset: 0.45 }, { transform: 'scale(1)' }], {
      duration: 900,
      easing: 'ease-in-out',
    });
    const start = performance.now();
    const duration = 800;
    const step = (/** @type {number} */ now) => {
      const t = Math.min(1, (now - start) / duration);
      const value = Math.round(from + (to - from) * easeOut(t));
      const text = template.replace('[price]', String(value));
      if (element.textContent !== text) element.textContent = text;
      if (t < 1) {
        this.pctFrame = requestAnimationFrame(step);
      } else {
        this.pctBusy = false;
      }
    };
    this.pctFrame = requestAnimationFrame(step);
  }

  /**
   * Paints the bar from the current total. Animations only play when `animate` is true and the total changed.
   * @param {boolean} animate
   */
  #render(animate) {
    if (this.milestones.length === 0) return;
    const total = this.cartQty + this.#choosing;
    const previous = this.lastTotal;
    this.lastTotal = total;

    const changed = previous !== null && previous !== total;
    // Only a real change decides the direction; a repaint in the middle of a move must not cancel the celebration.
    if (changed) this.movingUp = total > (previous ?? 0);
    const target = this.#posFor(total);

    // Several milestones can be passed at once (a held press). Only the highest new one celebrates.
    if (changed && this.movingUp) {
      let highest = -1;
      this.milestones.forEach((m, i) => {
        if (total >= m.qty && !this.celebrated.has(i)) highest = i;
      });
      this.celebrateIndex = highest;
    } else if (changed) {
      this.celebrateIndex = -1;
    }

    if (previous === null) {
      // First paint (or a repaint after the section was re-rendered): no animation, and nothing counts as new.
      this.#flowTo(target, false);
      this.unlockedSet.forEach((i) => this.celebrated.add(i));
    } else if (changed) {
      this.#flowTo(target, animate);
    } else {
      // Nothing changed, but the page may have been re-rendered: draw the saved state again.
      this.#paint();
      this.#syncStops(false);
    }

    const stops = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mix-stop]')]);
    const next = this.milestones.find((m) => total < m.qty) || null;
    const reached = this.milestones.filter((m) => total >= m.qty);
    const best = reached[reached.length - 1] || null;
    const bestIndex = best ? this.milestones.indexOf(best) : -1;
    const reduced = this.#reduced;

    stops.forEach((stop, index) => {
      const milestone = this.milestones[index];
      if (!milestone) return;
      const near = total < milestone.qty && next === milestone && milestone.qty - total === 1;
      stop.classList.toggle('is-near', near && !reduced);
    });

    const help = this.querySelector('[data-mix-help]');
    if (help) {
      let text;
      let isBest = false;
      if (!next) {
        text = this.dataset.bestText || 'Best saving unlocked';
        isBest = true;
      } else {
        const left = next.qty - total;
        let template;
        if (best) {
          const big = bestIndex >= 1;
          if (left === 1) template = big ? this.dataset.unlockedOneMoreBigText : this.dataset.unlockedOneMoreText;
          else template = big ? this.dataset.unlockedMoreBigText : this.dataset.unlockedMoreText;
        } else {
          template = left === 1 ? this.dataset.oneMoreText : this.dataset.moreText;
        }
        text = (template || '')
          .replace('[done]', String(best ? best.price : this.#basePrice))
          .replace('[count]', String(left))
          .replace('[price]', String(next.price));
      }
      this.#setText(help, text, previous !== null, isBest);
    }

    const totalOut = this.querySelector('[data-mix-total]');
    if (totalOut) this.#countTo(totalOut, total, previous !== null && animate);

    const pctOut = this.querySelector('[data-mix-percent]');
    if (pctOut) {
      const bestPct = best ? best.price : this.#basePrice;
      const text = (best ? this.dataset.priceText || '' : this.dataset.noneText || '').replace('[price]', String(bestPct));
      const reachedTop = !next && best && this.movingUp && changed && this.lastBestPct !== bestPct;
      if (changed && !reachedTop) {
        // The total changed in another way (for example spools were removed): stop any count-up and show the true value.
        cancelAnimationFrame(this.pctFrame);
        this.pctBusy = false;
      }
      if (reachedTop) {
        this.#countPercent(pctOut, this.lastBestPct, bestPct);
      } else if (!this.pctBusy) {
        this.#setText(pctOut, text, previous !== null);
      }
      this.lastBestPct = bestPct;
    }
  }
}

if (!customElements.get('surespool-mix-save')) {
  customElements.define('surespool-mix-save', SureSpoolMixSave);
}
